import React, { useMemo, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Flex, Text } from 'rebass/styled-components';
import { ProfilePicture, SocialIcons, Tag, Button, Tooltip } from '@getro/rombo';
import { Copy } from 'lucide-react';
import copy from 'copy-to-clipboard';
import capitalize from 'helpers/capitalize';
import { mapCompanyHeadCountIntervals } from 'helpers/organizations';
import NetworkSchema from '../../../schemas/network';
import { mapCompanyFundType } from '../../../services/organizationsV2/organizationFundTypes';

export const InfoView = ({ network, company, socials, logoUrl }) => {
  const { jobBoardFilters, features } = network;
  const { stageFilter, industryTagsFilter, companySizeFilter } = jobBoardFilters;

  const timer = useRef(null);
  const [copied, setCopied] = useState(false);
  const showCompanyId = features?.includes('display_company_id_in_ap');

  useEffect(() => () => clearTimeout(timer.current), []);

  const hasIndustryTags = useMemo(
    () => industryTagsFilter && company.industryTags?.length > 0,
    [industryTagsFilter, company.industryTags?.length],
  );

  const hasStageTags = useMemo(() => stageFilter && company.stage, [company.stage, stageFilter]);

  const hasCompanySizeTags = useMemo(
    () => companySizeFilter && company.approxEmployees,
    [company.approxEmployees, companySizeFilter],
  );

  const hasTopics = useMemo(() => company.topics?.length > 0, [company.topics?.length]);

  const hasTags = useMemo(
    () => hasIndustryTags || hasStageTags || hasCompanySizeTags || hasTopics,
    [hasCompanySizeTags, hasIndustryTags, hasStageTags, hasTopics],
  );

  return (
    <Flex flexWrap="wrap" sx={{ gap: '24px' }}>
      <Box minWidth="64px">
        <ProfilePicture variant="square" name={company.name} imageUrl={logoUrl} width="64px" height="64px" />
      </Box>
      <Box width="calc(100% - 88px)" flexBasis="330px" flexGrow="1">
        <Flex flexWrap="wrap" marginBottom="3" flexDirection="column" sx={{ gap: '8px' }}>
          <Flex flexWrap="wrap" sx={{ gap: '8px' }}>
            {company.name && (
              <Text as="h1" fontSize="3" fontWeight="semibold">
                {company.name}
              </Text>
            )}
            {!!company.domain && <Text color="text.main">{company.domain}</Text>}
          </Flex>
          {company?.locations?.length > 0 && (
            <Text fontSize="1" color="text.subtle">
              {company.locations.map((l) => l.name).join(` · `)}
            </Text>
          )}
          <Flex color="text.subtle" alignItems="center" sx={{ gap: '8px' }}>
            {company.founded && <Text fontSize="1">Founded in {company.founded}</Text>}
          </Flex>
        </Flex>
        {hasTags && (
          <Flex sx={{ mb: '8px', rowGap: '8px', flexWrap: 'wrap' }}>
            {hasIndustryTags && (
              <>
                {company.industryTags.map((tag) => (
                  <Tag key={tag} variant="transparent">
                    {capitalize(tag)}
                  </Tag>
                ))}
              </>
            )}
            {hasCompanySizeTags && (
              <Tag key={company.approxEmployees} variant="transparent">
                {mapCompanyHeadCountIntervals(company.approxEmployees)}
              </Tag>
            )}
            {hasStageTags && (
              <Tag key={company.stage} variant="transparent">
                {mapCompanyFundType(company.stage)}
              </Tag>
            )}
            {hasTopics && (
              <>
                {company.topics.map(({ id, name }) => (
                  <Tag key={id} variant="transparent">
                    {capitalize(name)}
                  </Tag>
                ))}
              </>
            )}
          </Flex>
        )}
        {company.description && (
          <Text fontSize="14px" color="text.main" marginBottom="3" className="text-break">
            {company.description}
          </Text>
        )}
        <SocialIcons {...socials} />
        {showCompanyId && (
          <>
            <Box sx={{ height: '1px', mt: '16px', mb: '8px', backgroundColor: 'border.subtle' }} />
            <Flex alignItems="center" sx={{ gap: '8px' }}>
              <Text fontSize="14px" color="text.subtle">
                Company id: {company.id || '-'}
              </Text>
              <Tooltip
                content="Copied"
                position="right"
                options={{
                  visible: copied,
                }}
                trigger={
                  <Button
                    variant="tertiary"
                    size="small"
                    icon={Copy}
                    onClick={() => {
                      copy(String(company.id || '-'));
                      setCopied(true);
                      timer.current = setTimeout(() => {
                        setCopied(false);
                      }, 2000);
                    }}
                    sx={{ color: 'text.subtle' }}
                  >
                    Copy
                  </Button>
                }
              />
            </Flex>
          </>
        )}
      </Box>
    </Flex>
  );
};

InfoView.propTypes = {
  network: NetworkSchema.isRequired,
  company: PropTypes.object.isRequired,
  socials: PropTypes.object.isRequired,
  logoUrl: PropTypes.string.isRequired,
};
